/**
 * Design System - Shadow Tokens
 * Современная система теней для CRM
 */

export const shadows = {
  // Мягкие современные тени
  none: 'none',
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',

  // Специальные тени (Figma Effect Styles)
  card: '0 2px 8px -2px rgba(13, 15, 26, 0.05)', // Shadow/Card
  cardHover: '0 4px 12px -2px rgba(13, 15, 26, 0.08)',
  button: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  buttonHover: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  buttonActive: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',

  // Тени для элементов интерфейса
  popover: '0 10px 28px -6px rgba(26, 31, 51, 0.14)', // Shadow/Popover
  dropdown: '0 10px 28px -6px rgba(26, 31, 51, 0.14)', // = popover
  modal: '0 18px 48px -12px rgba(26, 31, 51, 0.16)', // Shadow/Modal
  tooltip: '0 4px 6px -1px rgba(0, 0, 0, 0.15), 0 2px 4px -1px rgba(0, 0, 0, 0.1)',

  // Focus styles
  focus: {
    primary: '0 0 0 2px rgba(255, 255, 255, 0.8), 0 0 0 4px rgba(79, 70, 229, 0.3)',
    secondary: '0 0 0 2px rgba(255, 255, 255, 0.8), 0 0 0 4px rgba(16, 185, 129, 0.3)',
    error: '0 0 0 2px rgba(255, 255, 255, 0.8), 0 0 0 4px rgba(239, 68, 68, 0.3)',
    warning: '0 0 0 2px rgba(255, 255, 255, 0.8), 0 0 0 4px rgba(245, 158, 11, 0.3)',
  },

  // Inner shadows
  inner: {
    sm: 'inset 0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    md: 'inset 0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  },
} as const

// Legacy export для обратной совместимости
export const AppShadows = {
  ...shadows,
  xs: shadows.sm,
  xxl: shadows['2xl'],
  buttonPressed: shadows.buttonActive,
  focusError: shadows.focus.error,
} as const

// CSS Variables Generator
export const getShadowCSSVariables = () => {
  return {
    // Basic shadows
    '--shadow-none': shadows.none,
    '--shadow-sm': shadows.sm,
    '--shadow-base': shadows.base,
    '--shadow-md': shadows.md,
    '--shadow-lg': shadows.lg,
    '--shadow-xl': shadows.xl,
    '--shadow-2xl': shadows['2xl'],

    // Component shadows
    '--shadow-card': shadows.card,
    '--shadow-card-hover': shadows.cardHover,
    '--shadow-button': shadows.button,
    '--shadow-button-hover': shadows.buttonHover,
    '--shadow-button-active': shadows.buttonActive,
    '--shadow-dropdown': shadows.dropdown,
    '--shadow-modal': shadows.modal,
    '--shadow-tooltip': shadows.tooltip,

    // Focus shadows
    '--shadow-focus-primary': shadows.focus.primary,
    '--shadow-focus-secondary': shadows.focus.secondary,
    '--shadow-focus-error': shadows.focus.error,
    '--shadow-focus-warning': shadows.focus.warning,

    // Inner shadows
    '--shadow-inner-sm': shadows.inner.sm,
    '--shadow-inner-base': shadows.inner.base,
    '--shadow-inner-md': shadows.inner.md,
  }
}

export type ShadowKey = keyof typeof AppShadows

/**
 * Получить тень с указанной интенсивностью
 * @param level - Уровень тени ('xs' | 'sm' | 'md' | 'lg' | 'xl')
 * @returns CSS box-shadow значение
 */
export const getShadow = (level: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' = 'md'): string => {
  return AppShadows[level]
}
